import React, { useEffect, useState } from "react";
import axios from "axios";
import BusModifyForm from "./BusModifyForm";
import BusFilter from "./BusFilter";
import "./BusListing.css";
import { Col, Container, Row } from "react-bootstrap";
import { TiTickOutline } from "react-icons/ti";
import { LuArrowDownUp } from "react-icons/lu";
import { PiSortAscending } from "react-icons/pi";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import BusSeatLayout from "./BusSeatLayout";
import BusLoader from "./BusLoader";
import BusNotFound from "./BusNotFound";
import SortBus from "./BusFilter/SortBus";
import BusMobileModifyForm from "./BusMobileModifyForm";
import { PropagateLoader } from "react-spinners";
import BoardingDropping from "./BoardingDropping";

const BusListing = () => {
  const [activeTabBus, setActiveTabBus] = useState("");
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [busesData, setBusesData] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // const [showSeatLayout, setShowSeatLayout] = useState(false);
  const [seatLayoutData, setSeatLayoutData] = useState({});
  const [boardingDropping, setBoardingDropping] = useState({});

  const [allBuses, setAllBuses] = useState([]);
  const [filteredBuses, setFilteredBuses] = useState([]);
  const [priceLimits, setPriceLimits] = useState([0, 0]);
  const [selectedDepartureSlots, setSelectedDepartureSlots] = useState([]); // ["before6", "6to12", "12to18", "after18"]
  const [selectedSeatTypes, setSelectedSeatTypes] = useState([]); // ["Sleeper", "Seater"]
  const [selectedBusTypes, setSelectedBusTypes] = useState([]);
  const [seatLayoutLoading, setSeatLayoutLoading] = useState({});
  const [boardingLoading, setBoardingLoading] = useState({});

  const [traceId, setTraceId] = useState("");
  const [showSortModal, setShowSortModal] = useState(false);
  const [formattedDate, setFormattedDate] = useState("");
  const [results, setResults] = useState([]);
  const [showNotFound, setShowNotFound] = useState(false);
  const [mobileBoardingIndex, setMobileBoardingIndex] = useState(null);

  useEffect(() => {
    setSeatLayoutData({});
    setBoardingDropping({});
    setSeatLayoutLoading({});
    setBoardingLoading({});

    const OriginId = params.get("cityId");
    const DestinationId = params.get("desId");
    const journeyDate = params.get("journeyDate");

    if (!OriginId || !DestinationId || !journeyDate) return;

    const formattedDate = moment(journeyDate, "YYYY-MM-DD").format(
      "YYYY-MM-DD"
    );

    const requestData = {
      OriginId: OriginId,
      DestinationId: DestinationId,
      DateOfJourney: formattedDate,
    };
    console.log(requestData);

    // Store requestData if needed later
    // localStorage.setItem("busSearchData", JSON.stringify(requestData));

    const fetchBusDetails = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          "https://admin.travelsdata.com/api/Bus/Search",
          requestData
        );

        const busSearchResult = response.data?.data?.BusSearchResult;
        console.log("Search Data:", busSearchResult);

        if (!busSearchResult) {
          console.error(
            "BusSearchResult not found in response:",
            response.data
          );
          setError("No results found");
          return;
        }

        const buses = busSearchResult.BusResults || [];
        setAllBuses(buses);
        setFilteredBuses(buses);

        // set dynamic price range
        if (buses.length > 0) {
          const prices = buses.map(
            (bus) => bus.BusPrice?.PublishedPriceRoundedOff || 0
          );
          const minPrice = Math.min(...prices);
          const maxPrice = Math.max(...prices);
          setPriceLimits([minPrice, maxPrice]);
        }

        setBusesData(busSearchResult);
        setTraceId(busSearchResult.TraceId);
      } catch (err) {
        console.error("Bus Search API error:", err);
        setError("Something went wrong while fetching bus data");
      } finally {
        setLoading(false); //stop loader
      }
    };

    fetchBusDetails();
  }, [location.search]);

  // Apply Filters (just like CabListing)
  const applyFilters = (
    priceRange,
    busTypes,
    operators,
    boarding,
    dropping,
    departureSlots = [],
    seatTypes = []
  ) => {
    let filtered = allBuses;

    // Price filter
    filtered = filtered.filter(
      (bus) =>
        bus.BusPrice.PublishedPriceRoundedOff >= priceRange[0] &&
        bus.BusPrice.PublishedPriceRoundedOff <= priceRange[1]
    );

    // Bus Type filter (normalize to AC / Non-AC)
    if (busTypes.length > 0) {
      filtered = filtered.filter((bus) => {
        const type = bus.BusType?.toLowerCase().includes("a/c")
          ? "AC"
          : "Non-AC";
        return busTypes.includes(type);
      });
    }

    // Operators filter
    if (operators.length > 0) {
      filtered = filtered.filter((bus) => operators.includes(bus.TravelName));
    }

    // Boarding filter
    if (boarding.length > 0) {
      filtered = filtered.filter((bus) =>
        bus.BoardingPointsDetails.some((bp) =>
          boarding.includes(bp.CityPointName)
        )
      );
    }

    // Dropping filter
    if (dropping.length > 0) {
      filtered = filtered.filter((bus) =>
        bus.DroppingPointsDetails.some((dp) =>
          dropping.includes(dp.CityPointName)
        )
      );
    }

    // Departure time slots filter
    if (departureSlots.length > 0) {
      filtered = filtered.filter((bus) => {
        const depHour = moment(bus.DepartureTime).hour();
        return departureSlots.some((slot) => {
          if (slot === "before6") return depHour < 6;
          if (slot === "6to12") return depHour >= 6 && depHour < 12;
          if (slot === "12to18") return depHour >= 12 && depHour < 18;
          if (slot === "after18") return depHour >= 18;
          return false;
        });
      });
    }

    // Seat type filter (Sleeper / Seater)
    if (seatTypes.length > 0) {
      filtered = filtered.filter((bus) =>
        seatTypes.some((type) =>
          bus.BusType.toLowerCase().includes(type.toLowerCase())
        )
      );
    }

    setFilteredBuses(filtered);
  };

  const handleBookNowSeat = (resultIndex, index) => {
    if (!traceId) {
      console.error("TraceId is missing");
      return;
    }

    const payload = { TraceId: traceId, ResultIndex: resultIndex };

    // start loader
    setSeatLayoutLoading((prev) => ({ ...prev, [index]: true }));
    setBoardingLoading((prev) => ({ ...prev, [index]: true }));

    // Fetch Seat Layout
    axios
      .post("https://admin.travelsdata.com/api/Bus/SeatLayout", payload)
      .then((response) => {
        const seatLayout =
          response.data?.data?.GetBusSeatLayOutResult?.SeatLayoutDetails;

        if (response.data && response.data.success === true) {
          // close all others, only keep current bus open
          setSeatLayoutData({
            [index]: {
              show: true,
              data: seatLayout,
            },
          });

          // Fetch Boarding & Dropping points
          axios
            .post("https://admin.travelsdata.com/api/Bus/Boarding", payload)
            .then((res) => {
              const boarding =
                res.data?.data?.GetBusRouteDetailResult
                  ?.BoardingPointsDetails || [];
              const dropping =
                res.data?.data?.GetBusRouteDetailResult
                  ?.DroppingPointsDetails || [];

              setBoardingDropping((prev) => ({
                [index]: { boardingPoints: boarding, droppingPoints: dropping },
              }));
            })
            .finally(() => {
              setBoardingLoading((prev) => ({ ...prev, [index]: false }));
            });
        } else {
          console.error("SeatLayout API returned error", response.data);
        }
      })
      .catch((err) => {
        console.error("SeatLayout API error:", err);
      })
      .finally(() => {
        setSeatLayoutLoading((prev) => ({ ...prev, [index]: false }));
      });
  };

  const handleOpenMobileBoarding = async (bus, index) => {
    if (!traceId) return;
    const payload = { TraceId: traceId, ResultIndex: bus.ResultIndex };
    // Open modal immediately and show loader
    setMobileBoardingIndex(index);
    setBoardingLoading((prev) => ({ ...prev, [index]: true }));
    try {
      const res = await axios.post(
        "https://admin.travelsdata.com/api/Bus/Boarding",
        payload
      );
      const boarding =
        res.data?.data?.GetBusRouteDetailResult?.BoardingPointsDetails || [];
      const dropping =
        res.data?.data?.GetBusRouteDetailResult?.DroppingPointsDetails || [];
      setBoardingDropping((prev) => ({
        ...prev,
        [index]: { boardingPoints: boarding, droppingPoints: dropping },
      }));
    } catch (e) {
      console.error(e);
    } finally {
      setBoardingLoading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleOpenSort = () => setShowSortModal(true);
  const handleCloseSort = () => setShowSortModal(false);

  const amenitiesList = [
    "Emergency exit",
    "Fire Extinguisher",
    "GPS",
    "Water Bottle",
    "First Aid Box",
    "AC",
  ];

  const chipFilters = [
    { label: "6pm-12am", type: "departure", value: "after18" },
    { label: "AC", type: "busType", value: "AC" },
    { label: "Non-AC", type: "busType", value: "Non-AC" },
    { label: "Sleeper", type: "seatType", value: "sleeper" },
    { label: "Seater", type: "seatType", value: "seater" },
  ];

  const handleChipToggle = (chip) => {
    if (chip.type === "departure") {
      setSelectedDepartureSlots((prev) => {
        const updated = prev.includes(chip.value)
          ? prev.filter((v) => v !== chip.value)
          : [...prev, chip.value];

        applyFilters(
          priceLimits,
          selectedBusTypes,
          [],
          [],
          [],
          updated,
          selectedSeatTypes
        );
        return updated;
      });
    } else if (chip.type === "seatType") {
      setSelectedSeatTypes((prev) => {
        const updated = prev.includes(chip.value)
          ? prev.filter((v) => v !== chip.value)
          : [...prev, chip.value];

        applyFilters(
          priceLimits,
          selectedBusTypes,
          [],
          [],
          [],
          selectedDepartureSlots,
          updated
        );
        return updated;
      });
    } else if (chip.type === "busType") {
      setSelectedBusTypes((prev) => {
        const updated = prev.includes(chip.value)
          ? prev.filter((v) => v !== chip.value)
          : [...prev, chip.value];

        applyFilters(
          priceLimits,
          updated,
          [],
          [],
          [],
          selectedDepartureSlots,
          selectedSeatTypes
        );
        return updated;
      });
    }
  };

  // Format bus journey duration
  const formatDuration = (departureTime, arrivalTime) => {
    const departure = new Date(departureTime);
    const arrival = new Date(arrivalTime);

    let diffMs = arrival.getTime() - departure.getTime();

    // if arrival is earlier than departure, assume arrival next day
    if (diffMs < 0) {
      diffMs += 24 * 60 * 60 * 1000;
    }

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    const paddedHours = String(hours).padStart(2, "0");
    const paddedMinutes = String(minutes).padStart(2, "0");

    return days > 0
      ? `${days}d ${paddedHours}h ${paddedMinutes}m`
      : `${paddedHours}h ${paddedMinutes}m`;
  };

  const [sortField, setSortField] = useState("DEPARTURE");
  const [sortOrder, setSortOrder] = useState("asc");

  const handleSort = (field) => {
    const newOrder =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";

    setSortField(field);
    setSortOrder(newOrder);

    const sorted = [...filteredBuses].sort((a, b) => {
      let valA, valB;

      if (field === "DEPARTURE") {
        valA = new Date(a.DepartureTime);
        valB = new Date(b.DepartureTime);
      } else if (field === "DURATION") {
        const durA = new Date(a.ArrivalTime) - new Date(a.DepartureTime);
        const durB = new Date(b.ArrivalTime) - new Date(b.DepartureTime);
        valA = durA;
        valB = durB;
      } else if (field === "PRICE") {
        valA = a.BusPrice?.PublishedPriceRoundedOff || 0;
        valB = b.BusPrice?.PublishedPriceRoundedOff || 0;
      }

      if (newOrder === "asc") return valA > valB ? 1 : -1;
      else return valA < valB ? 1 : -1;
    });

    setFilteredBuses(sorted);
  };

  return (
    <div>
      <div className="Buslisting">
        <BusModifyForm />
        <div className="Buslisting-wrapper">
          <Container>
            <Row>
              <Col md={3}>
                <BusFilter
                  busData={allBuses}
                  priceLimits={priceLimits}
                  applyFilters={applyFilters}
                />
              </Col>

              <Col md={9}>
                <Row
                  style={{
                    margin: "0",
                    padding: "0",
                    background: "#fef3f3",
                    borderRadius: "5px 5px 0 0",
                  }}
                  className="d-md-flex row_backgorund_list_TG"
                >
                  <div className="Buslisting-header">
                    <div className="Buslisting-header-section Buslisting-header-times">
                      <Col md={3}>Departure</Col>
                      <Col md={3}>Duration</Col>
                      <Col md={3}>Arrival</Col>
                    </div>
                    <div className="Buslisting-header-section Buslisting-header-operator">
                      <Col md={6}>Bus Operator</Col>
                    </div>
                    <div className="Buslisting-header-section Buslisting-header-price">
                      <Col md={6}>Price</Col>
                    </div>
                  </div>
                </Row>
                {loading ? (
                  <BusLoader />
                ) : filteredBuses && filteredBuses.length !== 0 ? (
                  filteredBuses.map((bus, index) => (
                    <div className="Buslisting-card" key={index}>
                      <div className="Buslisting-main">
                        <Row>
                          <Col md={5}>
                            <div className="Buslisting-times">
                              <div className="Buslisting-time-block">
                                <div className="Buslisting-time">
                                  {new Date(
                                    bus.DepartureTime
                                  ).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                  })}
                                </div>
                                <div className="Buslisting-city">
                                  {busesData.Origin}
                                </div>
                                <div className="Buslisting-seats">
                                  {bus.AvailableSeats} Seat(s) left
                                </div>
                              </div>
                              <div className="Buslisting-duration">
                                {/* <div className="Buslisting-duration-text">05h 39m</div> */}
                                <div className="Buslisting-duration-text">
                                  {formatDuration(
                                    bus.DepartureTime,
                                    bus.ArrivalTime
                                  )}
                                </div>

                                <div className="Buslisting-dotted"></div>
                                {/* <div className="Buslisting-dot"></div> */}
                              </div>
                              <div className="Buslisting-time-block">
                                <div className="Buslisting-time">
                                  {new Date(bus.ArrivalTime).toLocaleTimeString(
                                    [],
                                    {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: false,
                                    }
                                  )}
                                </div>
                                <div className="Buslisting-city">
                                  {busesData.Destination}
                                </div>
                              </div>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="Buslisting-operator">
                              <div className="Buslisting-operator-name">
                                {bus.TravelName}
                              </div>
                              <div className="Buslisting-operator-type">
                                {bus.BusType}{" "}
                              </div>
                              <div className="Buslisting-rating">
                                {/* <span className="Buslisting-rating-badge">
                                  ⭐ 4.8
                                </span> */}
                                <span className="Buslisting-icons">
                                  <img
                                    src="/Images/buses/chair.png"
                                    alt="seat"
                                  />
                                  <img src="/Images/buses/ac.png" alt="ac" />
                                  <img
                                    src="/Images/buses/fire-extinguisher.png"
                                    alt=""
                                  />
                                </span>
                                <span className="Buslisting-extra">2+</span>
                              </div>
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="Buslisting-price">
                              <div className="Buslisting-price-text">
                                Starting From
                              </div>
                              <div className="Buslisting-price-strike">
                                ₹{(bus.BusPrice.PublishedPriceRoundedOff * 1.10).toFixed(0)}
                              </div>
                              <div className="Buslisting-price-final">
                                ₹{bus.BusPrice.PublishedPriceRoundedOff}
                              </div>

                              <button
                                className="Buslisting-button"
                                onClick={() =>
                                  handleBookNowSeat(bus.ResultIndex, index)
                                }
                              >
                                Select Seats
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </div>
                      <div className="Buslisting-belowItems">
                        <span className="Buslisting-belowItems-left">
                          <img src="/Images/buses/live.png" alt="" /> Live
                          Tracking
                        </span>
                        <div className="Buslisting-belowItems-right">
                          <ul className="Buslisting-belowItems-list">
                            <li
                              className={`Buslisting-belowItems-item ${
                                activeTabBus[index] === "boarding"
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() =>
                                setActiveTabBus((prev) => ({
                                  ...prev,
                                  [index]:
                                    prev[index] === "boarding"
                                      ? ""
                                      : "boarding",
                                }))
                              }
                            >
                              Boarding & Dropping Point
                            </li>

                            <li
                              className={`Buslisting-belowItems-item ${
                                activeTabBus[index] === "amenities"
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() =>
                                setActiveTabBus((prev) => ({
                                  ...prev,
                                  [index]:
                                    prev[index] === "amenities"
                                      ? ""
                                      : "amenities",
                                }))
                              }
                            >
                              Amenities
                            </li>

                            <li
                              className={`Buslisting-belowItems-item ${
                                activeTabBus[index] === "cancellation"
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() =>
                                setActiveTabBus((prev) => ({
                                  ...prev,
                                  [index]:
                                    prev[index] === "cancellation"
                                      ? ""
                                      : "cancellation",
                                }))
                              }
                            >
                              Cancellation Policy
                            </li>
                          </ul>
                        </div>
                      </div>

                      {activeTabBus[index] === "boarding" && (
                        <div className="Buslisting-boardingPoints">
                          <div className="Buslisting-boarding-column">
                            <h4>Boarding Points</h4>
                            <ul>
                              {bus.BoardingPointsDetails?.map((point, i) => (
                                <li key={i}>
                                  (
                                  {new Date(
                                    point.CityPointTime
                                  ).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                  })}
                                  ) {point.CityPointName}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="Buslisting-boarding-column">
                            <h4>Dropping Points</h4>
                            <ul>
                              {bus.DroppingPointsDetails?.map((point, i) => (
                                <li key={i}>
                                  (
                                  {new Date(
                                    point.CityPointTime
                                  ).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                  })}
                                  ) {point.CityPointName}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      {activeTabBus[index] === "amenities" && (
                        <div className="Buslisting-amenities">
                          <h4>List Of Amenities You Will Get</h4>
                          <div className="Buslisting-amenities-grid">
                            {amenitiesList.map((amenity, i) => (
                              <div key={i} className="Buslisting-amenity-item">
                                <TiTickOutline className="Buslisting-AmenitiesIcon" />
                                <span>{amenity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {activeTabBus[index] === "cancellation" && (
                        <div className="Buslisting-cancellation">
                          <div className="Buslisting-cancellation-left">
                            <table className="Buslisting-cancellation-table">
                              <thead>
                                <tr>
                                  <th>Cancellation Time</th>
                                  <th>Cancellation Charges</th>
                                </tr>
                              </thead>
                              <tbody>
                                {bus.CancellationPolicies.map((item, i) => {
                                  let fromTime = new Date(item.FromDate);
                                  let toTime = new Date(item.ToDate);
                                  let timeStr;

                                  if (i === 0) {
                                    timeStr = `Till ${fromTime.toLocaleTimeString(
                                      [],
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                      }
                                    )}, ${fromTime.toDateString()}`;
                                  } else if (
                                    i ===
                                    bus.CancellationPolicies.length - 1
                                  ) {
                                    timeStr = `After ${fromTime.toLocaleTimeString(
                                      [],
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                      }
                                    )}, ${fromTime.toDateString()}`;
                                  } else {
                                    timeStr = `Between ${fromTime.toLocaleTimeString(
                                      [],
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                      }
                                    )}, ${fromTime.toDateString()} - ${toTime.toLocaleTimeString(
                                      [],
                                      {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                      }
                                    )}, ${toTime.toDateString()}`;
                                  }

                                  return (
                                    <tr key={i}>
                                      <td>{timeStr}</td>
                                      <td>{item.CancellationCharge}%</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div className="Buslisting-cancellation-right">
                            <p>
                              Cancellation charges are calculated on a per
                              passenger/Seat basis. The above cancellation
                              charge is calculated based on the starting seat
                              fare of ₹{bus.BusPrice.PublishedPriceRoundedOff}
                            </p>
                            <p>
                              Cancellation charges are calculated based on
                              service start date (origin point) + time at:{" "}
                              {new Date(bus.DepartureTime).toLocaleDateString(
                                "en-GB",
                                {
                                  weekday: "long",
                                  day: "2-digit",
                                  month: "long",
                                  year: "numeric",
                                }
                              )}{" "}
                              {new Date(bus.DepartureTime).toLocaleTimeString(
                                [],
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                }
                              )}
                            </p>

                            <p>
                              You cannot cancel the ticket after scheduled bus
                              departure time from the first origin point.
                            </p>
                          </div>
                        </div>
                      )}
                      {/* {seatLayoutData[index]?.show && (
                        <div className="Buslisting-seatLayout-wrapper">
                          <BusSeatLayout
                            seatData={seatLayoutData[index]?.data}
                            boardingDropping={boardingDropping[index]}
                          />
                        </div>
                      )} */}

                      {seatLayoutLoading[index] ? (
                        <div className="Buslisting-seatLoader">
                          <PropagateLoader color="#1a4f87" />
                        </div>
                      ) : (
                        seatLayoutData[index]?.show && (
                          <div className="Buslisting-seatLayout-wrapper">
                            <BusSeatLayout
                              seatData={seatLayoutData[index]?.data}
                              boardingDropping={boardingDropping[index]}
                              boardingLoader={boardingLoading[index]}
                              busInfo={{
                                ArrivalTime: bus.ArrivalTime,
                                BusType: bus.BusType,
                                DepartureDate: bus.DepartureDate,
                                DepartureTime: bus.DepartureTime,
                                Destination: busesData?.Destination || "",
                                Origin: busesData?.Origin || "",
                                TravelName: bus.TravelName,
                                ResultIndex: bus.ResultIndex,
                                TraceId: traceId,
                              }}
                            />
                          </div>
                        )
                      )}
                    </div>
                  ))
                ) : (
                  <BusNotFound />
                )}
              </Col>
            </Row>
          </Container>
        </div>
      </div>

      {/* Mobile View */}
      <div className="BuslistingMobile">
        <div className="BuslistingMobile-topHeader">
          <BusMobileModifyForm
            origin={busesData?.Origin}
            destination={busesData?.Destination}
            journeyDate={params.get("journeyDate")}
          />

          {/* Sort Tabs */}
          <div
            className="BuslistingMobile-sortTabs"
            style={{ marginTop: "12px" }}
          >
            <div
              className={sortField === "DEPARTURE" ? "active" : ""}
              onClick={() => handleSort("DEPARTURE")}
            >
              DEPARTURE{" "}
              <LuArrowDownUp
                style={{
                  transform:
                    sortField === "DEPARTURE" && sortOrder === "desc"
                      ? "rotate(180deg)"
                      : "none",
                  transition: "0.2s",
                }}
              />
            </div>

            <div
              className={sortField === "DURATION" ? "active" : ""}
              onClick={() => handleSort("DURATION")}
            >
              DURATION{" "}
              <LuArrowDownUp
                style={{
                  transform:
                    sortField === "DURATION" && sortOrder === "desc"
                      ? "rotate(180deg)"
                      : "none",
                  transition: "0.2s",
                }}
              />
            </div>

            <div
              className={sortField === "PRICE" ? "active" : ""}
              onClick={() => handleSort("PRICE")}
            >
              PRICE{" "}
              <LuArrowDownUp
                style={{
                  transform:
                    sortField === "PRICE" && sortOrder === "desc"
                      ? "rotate(180deg)"
                      : "none",
                  transition: "0.2s",
                }}
              />
            </div>
          </div>

          {/* Results + Recommended Toggle */}
          <div className="BuslistingMobile-resultsFilterRow" style={{padding:'0px 12px'}}>
            <span className="BuslistingMobile-busCount">
              {filteredBuses?.length || 0} Buses Found
            </span>

            <div className="BuslistingMobile-toggleWrap">
              <span>Recommended</span>
              <div className="BuslistingMobile-toggle">
                <div className="BuslistingMobile-toggle-circle" />
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="BuslistingMobile-filterBar">
            <div className="BuslistingMobile-chipList">
              {chipFilters.map((chip) => {
                const isActive =
                  (chip.type === "departure" &&
                    selectedDepartureSlots.includes(chip.value)) ||
                  (chip.type === "seatType" &&
                    selectedSeatTypes.includes(chip.value)) ||
                  (chip.type === "busType" &&
                    selectedBusTypes.includes(chip.value));

                return (
                  <div
                    key={chip.value}
                    className={`BuslistingMobile-chip ${
                      isActive ? "active" : ""
                    }`}
                    onClick={() => handleChipToggle(chip)}
                  >
                    {chip.label}
                  </div>
                );
              })}
            </div>

            {/* Sort Button */}
            <div
              className="BuslistingMobile-chip BuslistingMobile-sortChip"
              onClick={handleOpenSort}
            >
              <div className="BuslistingMobile-chipIcon">
                <PiSortAscending />
              </div>
              Sort Filters
            </div>

            {/* Sort Modal */}
            <SortBus
              show={showSortModal}
              closeFilter={handleCloseSort}
              clearAllFilters={() => setFilteredBuses(allBuses)}
              buses={allBuses}
              priceLimits={priceLimits}
              onApplyFilters={applyFilters}
            />
          </div>
        </div>

        {/* Loader / Results */}
        {loading ? (
          <BusLoader />
        ) : filteredBuses && filteredBuses.length > 0 ? (
          filteredBuses.map((bus, index) => (
            <div
              className="BuslistingMobile-card"
              key={bus.ResultIndex || index}
            >
              {/* <div className="BuslistingMobile-recommended">Recommended</div> */}

              {/* Card Top */}
              <div className="BuslistingMobile-cardTop">
                <div className="BuslistingMobile-operator">
                  <strong>{bus.TravelName}</strong>
                  <div style={{ color: "black" }}>{bus.BusType}</div>
                  {/* <div className="BuslistingMobile-rating">
                    <span className="BuslistingMobile-star">★ 4.0</span>
                  </div> */}
                </div>

                <div className="BuslistingMobile-priceSection">
                  <div className="BuslistingMobile-startingFrom">
                    Starting From
                  </div>
                  <div className="BuslistingMobile-price">
                    ₹{bus.BusPrice.PublishedPriceRoundedOff}
                  </div>
                  <div className="BuslistingMobile-seatsLeft">
                    {bus.AvailableSeats} Seat(s) left
                  </div>
                </div>
              </div>

              {/* Timings */}
              <div className="BuslistingMobile-timings">
                <span>
                  {new Date(bus.DepartureTime).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })}
                </span>

                <span className="BuslistingMobile-duration">
                  {formatDuration(bus.DepartureTime, bus.ArrivalTime)}
                </span>

                <span>
                  {new Date(bus.ArrivalTime).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })}
                </span>
              </div>

              {/* Icons */}
              <div className="BuslistingMobile-iconsRow">
                <img src="/Images/buses/chair.png" alt="seat" />
                <img src="/Images/buses/ac.png" alt="ac" />
                <img
                  src="/Images/buses/fire-extinguisher.png"
                  alt="fire safety"
                />
                <span className="BuslistingMobile-seatsCount">2+</span>
                <div className="BuslistingMobile-liveTracking">
                  <img src="/Images/buses/live.png" alt="live" />
                  Live Tracking
                </div>
              </div>

              {/* Footer */}
              <div className="BuslistingMobile-footerRow">
                <div className="BuslistingMobile-footer BuslistingMobile_mobile_dropping">
                  <div onClick={() => handleOpenMobileBoarding(bus, index)}>
                    <span className="BuslistingMobile-dot" /> Boarding &
                    Dropping Point
                  </div>
                  {/* <div>
                     <span className="BuslistingMobile-dot" /> Cancellation Policy
                   </div> */}
                  {/* <span className="BuslistingMobile-dot" /> Boarding & Dropping Point */}
                  {/* <span className="BuslistingMobile-dot" /> Boarding & Dropping Point */}
                </div>
                <button
                  className="Buslisting-button"
                  onClick={() => handleBookNowSeat(bus.ResultIndex, index)}
                >
                  Select Seats
                </button>
              </div>

              {/* Seat Layout */}
              {seatLayoutLoading[index] ? (
                <div className="Buslisting-seatLoader">
                  <PropagateLoader color="#1a4f87" />
                </div>
              ) : (
                seatLayoutData[index]?.show && (
                  <div className="Buslisting-seatLayout-wrapper">
                    <BusSeatLayout
                      seatData={seatLayoutData[index]?.data}
                      boardingDropping={boardingDropping[index]}
                      boardingLoader={boardingLoading[index]}
                      busInfo={{
                        ArrivalTime: bus.ArrivalTime,
                        BusType: bus.BusType,
                        DepartureDate: bus.DepartureDate,
                        DepartureTime: bus.DepartureTime,
                        Destination: busesData?.Destination || "",
                        Origin: busesData?.Origin || "",
                        TravelName: bus.TravelName,
                        ResultIndex: bus.ResultIndex,
                        TraceId: traceId,
                      }}
                    />
                  </div>
                )
              )}
            </div>
          ))
        ) : (
          <BusNotFound />
        )}
      </div>
      <BoardingDropping
        show={mobileBoardingIndex !== null}
        closeFilter={() => setMobileBoardingIndex(null)}
        boardingPoints={
          boardingDropping[mobileBoardingIndex]?.boardingPoints || []
        }
        droppingPoints={
          boardingDropping[mobileBoardingIndex]?.droppingPoints || []
        }
        loading={boardingLoading[mobileBoardingIndex]}
      />
    </div>
  );
};

export default BusListing;
